import React, { useState, useRef } from "react";
import { format } from "date-fns";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  alpha,
  Tooltip,
} from "@mui/material";
import { getFinancialYearAndYA } from "../utils/dateUtils";
import { formatAmount } from "../../../../../../utils/hooks/dataHandler";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../../../utils/export/componentExporter";
import RateCalculationDisplay from "./RateCalculationDisplay";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const totalRowStyle = {
  fontWeight: 700,
  color: "text.primary",
  py: 2,
  borderTop: "2px solid",
  borderColor: (theme) => alpha(theme.palette.divider, 0.3),
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
};

const RepaymentResultTable = ({
  schedule,
  validation,
  fyEndMonth,
  inAdvance,
  onImportClick,
  showImportButton = false,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "HP Repayment Schedule");
    }
  };

  if (!schedule || schedule.length === 0) {
    return null;
  }

  const formatDate = (date) => {
    return format(new Date(date), "dd MMM yyyy");
  };

  // Calculate YA for each row if FY end month is available
  const getYAForRow = (row) => {
    if (!fyEndMonth) return "-";
    try {
      // Special handling for deposit (instalment 0) when payments are "In Arrears"
      if (row.instalmentNo === 0 && !inAdvance) {
        const depositMonth = new Date(row.dueDate).getMonth() + 1;
        const depositYear = new Date(row.dueDate).getFullYear();

        if (depositMonth === fyEndMonth) {
          return depositYear + 2; // FY + 1 = YA
        }
      }

      const { ya } = getFinancialYearAndYA(row.dueDate, fyEndMonth);
      return ya;
    } catch (error) {
      return "-";
    }
  };

  const copyTableToClipboard = async () => {
    try {
      const headers = fyEndMonth
        ? [
            "Instalment No.",
            "Due Date",
            "YA",
            "Opening Balance",
            "Principal",
            "Interest",
            "Instalment",
            "Closing Balance",
          ]
        : [
            "Instalment No.",
            "Due Date",
            "Opening Balance",
            "Principal",
            "Interest",
            "Instalment",
            "Closing Balance",
          ];
      const rows = schedule.map((row) => {
        const baseRow = [
          row.instalmentNo,
          formatDate(row.dueDate),
          formatAmount(row.openingBalance, false),
          formatAmount(row.principal, false),
          formatAmount(row.interest, false),
          formatAmount(row.instalment, false),
          formatAmount(row.closingBalance, false),
        ];
        if (fyEndMonth) {
          baseRow.splice(2, 0, getYAForRow(row));
        }
        return baseRow;
      });

      const totalPrincipal = schedule.reduce(
        (sum, row) => sum + Number(row.principal.toFixed(2)),
        0
      );
      const totalInterest = schedule.reduce(
        (sum, row) => sum + Number(row.interest.toFixed(2)),
        0
      );
      const totalInstalment = schedule.reduce(
        (sum, row) => sum + Number(row.instalment.toFixed(2)),
        0
      );
      rows.push([
        "Totals",
        "",
        "",
        formatAmount(totalPrincipal, false),
        formatAmount(totalInterest, false),
        formatAmount(totalInstalment, false),
        "",
      ]);

      const csvContent = [
        headers.join("\t"),
        ...rows.map((row) => row.join("\t")),
      ].join("\n");

      await navigator.clipboard.writeText(csvContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy table:", err);
      alert("Failed to copy table. Please try again.");
    }
  };

  return (
    <Box ref={exportRef}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant='h6' fontWeight={600}>
          HP Repayment Schedule
        </Typography>
        <Box
          className='no-export'
          sx={{ display: "flex", gap: 1, alignItems: "center" }}
        >
          <CameraExportButton
            onClick={handleExportAsImage}
            tooltipTitle='Screenshot of the HP repayment schedule'
          />
          <Button
            onClick={copyTableToClipboard}
            variant='outlined'
            size='small'
            startIcon={<CopyAllIcon />}
          >
            {copySuccess ? "✓ Copied!" : "Copy Table"}
          </Button>
          {showImportButton && onImportClick && (
            <Tooltip title='Confirm and apply this repayment schedule to your Hire Purchase schedule'>
              <Button
                onClick={onImportClick}
                variant='contained'
                color='primary'
                size='small'
                startIcon={<CheckCircleOutlineIcon />}
              >
                Use This Schedule
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {schedule.rateCalculationSteps &&
        schedule.calculatedRate !== undefined && (
          <RateCalculationDisplay calculatedRate={schedule.calculatedRate} />
        )}

      {validation && !validation.valid && (
        <Alert
          severity='error'
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
          }}
        >
          <Typography variant='subtitle2' gutterBottom>
            <strong>Reconciliation Errors:</strong>
          </Typography>
          <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.5rem" }}>
            {validation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.divider, 0.1),
          overflow: "auto",
          maxWidth: "100%",
          "& .MuiTable-root": {
            minWidth: "max-content",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Instalment No.
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Due Date
              </TableCell>
              {fyEndMonth && (
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    borderBottom: "2px solid",
                    borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                    py: 2,
                  }}
                >
                  YA
                </TableCell>
              )}
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Opening Balance
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Principal
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Interest
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Instalment
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                  py: 2,
                }}
              >
                Closing Balance
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedule.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    color: "text.primary",
                    py: 1.5,
                  }}
                >
                  {row.instalmentNo}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 500,
                    color: "text.primary",
                    py: 1.5,
                  }}
                >
                  {formatDate(row.dueDate)}
                </TableCell>
                {fyEndMonth && (
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      py: 1.5,
                    }}
                  >
                    {getYAForRow(row)}
                  </TableCell>
                )}
                <TableCell align='right' sx={{ py: 1.5 }}>
                  {formatAmount(row.openingBalance, false)}
                </TableCell>
                <TableCell align='right' sx={{ py: 1.5 }}>
                  {formatAmount(row.principal, false)}
                </TableCell>
                <TableCell align='right' sx={{ py: 1.5 }}>
                  {formatAmount(row.interest, false)}
                </TableCell>
                <TableCell align='right' sx={{ py: 1.5 }}>
                  {formatAmount(row.instalment, false)}
                </TableCell>
                <TableCell align='right' sx={{ py: 1.5 }}>
                  {formatAmount(row.closingBalance, false)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                colSpan={fyEndMonth ? 4 : 3}
                sx={totalRowStyle}
                align='right'
              >
                Totals
              </TableCell>
              <TableCell align='right' sx={totalRowStyle}>
                {formatAmount(
                  schedule.reduce((sum, row) => sum + Number(row.principal.toFixed(2)), 0),
                  false
                )}
              </TableCell>
              <TableCell align='right' sx={totalRowStyle}>
                {formatAmount(
                  schedule.reduce((sum, row) => sum + Number(row.interest.toFixed(2)), 0),
                  false
                )}
              </TableCell>
              <TableCell align='right' sx={totalRowStyle}>
                {formatAmount(
                  schedule.reduce((sum, row) => sum + Number(row.instalment.toFixed(2)), 0),
                  false
                )}
              </TableCell>
              <TableCell sx={totalRowStyle}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {validation && validation.valid && showImportButton && onImportClick && (
        <Box
          className='no-export'
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.success.main, 0.2),
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            Everything looks good. You can apply this repayment to Hire Purchase
            schedule by clicking the <strong>"Use This Schedule"</strong>{" "}
            button.
          </Typography>
        </Box>
      )}

      {validation &&
        validation.reconciliation &&
        (!validation.valid ||
          validation.reconciliation.principalDifference > 0.01 ||
          validation.reconciliation.interestDifference > 0.01) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='h6' fontWeight={600} gutterBottom>
              Reconciliation Details
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                mt: 2,
              }}
            >
              <Table size='small'>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.03),
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        borderBottom: "2px solid",
                        borderColor: (theme) =>
                          alpha(theme.palette.divider, 0.1),
                        py: 2,
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        borderBottom: "2px solid",
                        borderColor: (theme) =>
                          alpha(theme.palette.divider, 0.1),
                        py: 2,
                      }}
                    >
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ py: 1.5 }}>Sum of Principal:</TableCell>
                    <TableCell align='right' sx={{ py: 1.5 }}>
                      {formatAmount(validation.reconciliation.sumPrincipal)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 1.5 }}>
                      Expected Principal (PV):
                    </TableCell>
                    <TableCell align='right' sx={{ py: 1.5 }}>
                      {formatAmount(
                        validation.reconciliation.expectedPrincipal
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={totalRowStyle}>Difference:</TableCell>
                    <TableCell align='right' sx={totalRowStyle}>
                      {formatAmount(
                        validation.reconciliation.principalDifference
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 1.5 }}>Sum of Interest:</TableCell>
                    <TableCell align='right' sx={{ py: 1.5 }}>
                      {formatAmount(validation.reconciliation.sumInterest)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 1.5 }}>Expected Interest:</TableCell>
                    <TableCell align='right' sx={{ py: 1.5 }}>
                      {formatAmount(validation.reconciliation.expectedInterest)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={totalRowStyle}>Difference:</TableCell>
                    <TableCell align='right' sx={totalRowStyle}>
                      {formatAmount(
                        validation.reconciliation.interestDifference
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
    </Box>
  );
};

export default RepaymentResultTable;
